import { Visibility } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import ActionButton from "Common/Components/Buttons/ActionButton";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import ListingPageContainer from "Common/Components/PageContainer/ListingPageContainer";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { usePermission } from "hooks/usePermission";
import {
  useEffect,
  useState
} from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportSupplierCSV,
  fetchSuppliers,
  updateStatusInSupplier,
} from "slices/Masters/supplier/thunk";
const SupplierList = () => {
  document.title = "Supplier List";
  const dispatch = useDispatch<any>();
  const [tableInstance, setTableInstance] = useState<any>();
  // Redux selector for category state
  const selectCategorys = createSelector(
    (state: any) => state.SupplierReducer,
    (data) => ({
      list: data.list,
      rowCount: data.rowCount,
      loading: data.loading,
      error: data.error,
    })
  );

  const { list, rowCount, loading, error } = useSelector(selectCategorys);
  

  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.supplier
  );



  // const [pageIndex, setPageIndex] = usePersistedPageIndex(
  //   "categoryPageIndex",
  //   0
  // );
  // const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);
  // const [columnFilters, setColumnFilters] = useState<any>();
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);

  const tableState = usePreservedTableState({
    key: TableKeys.MASTER.SUPPLIER,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(
      fetchSuppliers({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, pageIndex, pageSize, sorting, search, filterValue]);

  useEffect(() => {
    setData(list);
  }, [list]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);


  const handleStatusToggle = async (pkg: any) => {
    try {
      const updatedStatus = !pkg.is_active;
      setIsStatusUpdateLoading(true);
      const result = await dispatch(updateStatusInSupplier(pkg.supplier_id));

      // If we get here, the thunk fulfilled (succeeded)
      toast.success(
        `Supplier ${updatedStatus ? "activated" : "deactivated"} successfully`
      );
      dispatch(
        fetchSuppliers({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      );
    } catch (err: any) {
      toast.error("Failed to update status");
    } finally {
      setIsStatusUpdateLoading(false);
    }
  };

  const DynamicAction = canUpdate || canView
    ? [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const menuItems = [
            {
              icon: EditIcon,
              label: "Edit",
              permission: canUpdate,
              link: `/master/edit-supplier/${row.original.supplier_id}`,
              color: "primary",
            },
            {
              icon: Visibility,
              label: "View details",
              permission: canView,
              link: `/master/view-supplier/${row.original.supplier_id}`,
              color: "primary",
            },
          ];
          return <DynamicMenuItem row={row.original} menuItems={menuItems} />;
        },
      },
    ]
    : [];

  const columns = [
    {
      header: "Sr. No.",
      accessorKey: "sr_no",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Supplier Name",
      accessorKey: "supplier_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.SUPPLIER?.FILTER_DROPDOWN(
        "supplier_name"
      )}`,
    },
    {
      header: "Supplier Type",
      accessorKey: "supplier_type",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.SUPPLIER?.FILTER_DROPDOWN(
        "supplier_type"
      )}`,
    },
    {
      header: "Onboarding Date",
      accessorKey: "on_boarding_date",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => 
        <FormattedDate date={row.original?.on_boarding_date} />
    },
    {
      header: "Email",
      accessorKey: "email",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.SUPPLIER?.FILTER_DROPDOWN(
        "email"
      )}`,
    },
    {
      header: "Phone No.",
      accessorKey: "phone_number",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.SUPPLIER?.FILTER_DROPDOWN(
        "phone_number"
      )}`,
    },
    {
      header: "Status",
      accessorKey: "is_active",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const pkg = row.original;
        return (
          <div
            style={{ fontSize: "1.5rem", cursor: "pointer" }}
            onClick={() => handleStatusToggle(pkg)}
          >
            <i
              className={`bi ${pkg.is_active
                  ? "bi-toggle-on text-success"
                  : "bi-toggle-off text-danger"
                }`}
            />
          </div>
        );
      },
    },

    {
      header: "Created By/Date",
      accessorKey: "created_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => {
        const user = row.original.created_by_user;
        return (
          <NameWithDate
            name={user?.username}
            date={row.original.created_at}
          />
        );
      },
    },
    {
      header: "Updated By/Date",
      accessorKey: "updated_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => {
        const user = row.original.updated_by_user;
        const displayName = user
          ? typeof user === 'object'
            ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || 'N/A'
            : user
          : 'N/A';
        return (
          <NameWithDate
            name={user?.username}
            date={row.original.updated_at}
          />
        );
      },
    },
    ...DynamicAction,
  ];

  return (
    <ListingPageContainer>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card className="shadow-none">
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Supplier</h6>
                </Col>
                <Row className="align-items-center justify-content-between mt-1 px-0 g-3 flex-wrap">
                  {/* Search Box */}
                  <SearchBox
                    value={searchInput}
                    onChange={(e: any) => setSearchInput(e.target.value)}
                    placeholder="Search..."
                  />

                  {/* Column Visibility + Actions */}
                  <Col xs={12} md={12} lg={8} xl={6} className="">
                    <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 w-100">
                      {/* Left side: Column Visibility */}
                      <div className="">
                        {tableInstance && (
                          <ColumnVisibilityDropdown
                            columns={tableInstance.getAllLeafColumns()}
                            excludedColumns={["action", "sr_no"]}
                          />
                        )}
                      </div>

                      <div className="">
                        {canView && (
                        <ExportButton
                          onExport={async () => {
                            return await exportSupplierCSV({
                              search,
                              filters: filterValue,
                            });
                          }}
                        />
                        )}
                      </div>

                      <div>
                        {canCreate && (
                        <ActionButton
                          label="Add Supplier"
                          icon="bi bi-plus-circle"
                          navigateLink="/master/add-supplier"
                          variant="primary"
                        />
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper
                loading={loading || isStatusUpdateLoading}
                error={error}
              >
                <MasterTableContainer
                  columns={columns}
                  data={data}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  manualSorting={true}
                  colValue={setFilterValue}
                  onTableReady={setTableInstance}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default SupplierList;
